import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, DatePipe } from '@angular/common';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize, Subscription } from 'rxjs';
import { QuizMetadata } from '@edtech-ui/core/models/quiz-creation/quiz-meta-data';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'app-quiz-dialog',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, DatePipe, MatProgressSpinnerModule, MatTooltipModule],
	templateUrl: './quiz-dialog.component.html',
	styleUrl: './quiz-dialog.component.scss'
})
export class QuizDialogComponent implements OnInit, OnDestroy {
	quizData: QuizMetadata | null = null;
	isLoading: boolean = false;
	isStarting: boolean = false;
	subscription = new Subscription();

	constructor(
		public dialogRef: MatDialogRef<QuizDialogComponent>,
		private router: Router,
		private quizService: QuizManagementService,
		private toastr: ToastrService,
		@Inject(MAT_DIALOG_DATA) public data: { quizId: string, classId: number, courseId: string }
	) { }

	ngOnInit(): void {
		this.getQuizMetaData();
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	startQuiz(quizId: string | undefined): void {
		if (!quizId) return;

		this.isStarting = true;

		this.subscription.add(
			this.quizService.startQuizAttempt(quizId).pipe(
				finalize(() =>
					this.isStarting = false)
			).subscribe({
				next: (response) => {
					if (response.Success && response.Data?.attempt_id) {
						this.quizService.quizAttemptId.next(response.Data.attempt_id);
						localStorage.setItem('attempt_id', response.Data.attempt_id);
						this.router.navigate([`/student-dashboard/quiz/${quizId}`, { attemptId: response.Data.attempt_id }]);
						this.dialogRef.close({ started: true, quizId, attemptId: response.Data.attempt_id });
					} else {
						this.toastr.error('Failed to start quiz');
					}
				},
				error: () => {
					this.toastr.error('Error starting quiz');
				}
			})
		);
	}

	getQuizMetaData(): void {
		this.isLoading = true;
		if (this.data.quizId) {
			this.subscription.add(
				this.quizService.getQuizMetaData(this.data.quizId)
					.pipe(finalize(() => this.isLoading = false))
					.subscribe({
						next: (response) => {
							if (response.Success && response.Data) {
								this.quizData = response.Data;
							} else {
								this.toastr.error(response.Message);
							}
						},
						error: (err) => {
							this.toastr.error('Error fetching quiz metadata. Please try again.', 'Error', { timeOut: 4000 });
						}
					}));
		}
	}

	formatTimeLimit(seconds: number | undefined): string {
		if (!seconds || seconds <= 0) {
			return 'No Limit';
		}
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainingSeconds = seconds % 60;
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}